import { type ReactNode } from 'react';
import {
  AlertStatusIcon,
  CrossStatusIcon,
  InfoStatusIcon,
  VerifiedStatusIcon,
} from '@bravemove/design-tokens/icons';
import { useI18n } from '@bravemove/i18n/react/core';
import { cn } from '../../utils/cn';

/**
 * F09 v2 owner-facing verification state (declared locally — ui-web does not
 * depend on `@bravemove/types`). This pill is for the OWNER's own status
 * surfaces; public surfaces render only the blue `VerifiedTick`.
 */
export const OWNER_VERIFICATION_STATES = [
  'not_requested',
  'draft',
  'pending',
  'more_info_needed',
  'verified',
  'rejected',
  'revoked',
] as const;
export type OwnerVerificationState = (typeof OWNER_VERIFICATION_STATES)[number];

/**
 * Accepted `status` values: every `OwnerVerificationState`, plus the legacy
 * F33 value `'unverified'` (back-compat — rendered exactly as
 * `'not_requested'`).
 */
export type VerificationStatus = OwnerVerificationState | 'unverified';

export interface VerificationBadgeProps {
  readonly status?: VerificationStatus | undefined;
  readonly size?: 'sm' | 'md';
  readonly className?: string | undefined;
}

const NEUTRAL = 'bg-ink-3/10 text-ink border-ink-3/25';
const INFO = 'bg-info/10 text-ink border-info/30';

const TONE: Record<OwnerVerificationState, string> = {
  not_requested: NEUTRAL,
  draft: NEUTRAL,
  pending: INFO,
  more_info_needed: 'bg-warning/10 text-ink border-warning/30',
  verified: INFO,
  rejected: 'bg-danger/10 text-ink border-danger/30',
  revoked: 'bg-danger/15 text-ink border-danger/50',
};

/** English fallbacks — must match the frozen `verification:badge.state.*` contract. */
const LABEL_DEFAULTS: Record<OwnerVerificationState, string> = {
  not_requested: 'Not verified',
  draft: 'Draft',
  pending: 'In review',
  more_info_needed: 'More info needed',
  verified: 'Verified',
  rejected: 'Not approved',
  revoked: 'Revoked',
};

const SIZE: Record<'sm' | 'md', string> = {
  sm: 'h-[18px] px-1.5 text-[9.5px] gap-1',
  md: 'h-6 px-2.5 text-[11px] gap-1.5',
};
const GLYPH: Record<'sm' | 'md', number> = { sm: 10, md: 13 };

/** Map the legacy `'unverified'` value onto `'not_requested'`. */
export function normalizeVerificationStatus(status: VerificationStatus): OwnerVerificationState {
  return status === 'unverified' ? 'not_requested' : status;
}

function glyphFor(state: OwnerVerificationState, g: number): ReactNode {
  switch (state) {
    case 'pending':
      return (
        <span
          className="inline-block rounded-full bg-current motion-safe:animate-pulse"
          style={{ width: g - 3, height: g - 3 }}
          aria-hidden
        />
      );
    case 'verified':
      return <VerifiedStatusIcon size={g} color="currentColor" />;
    case 'more_info_needed':
      return <AlertStatusIcon size={g} color="currentColor" />;
    case 'rejected':
    case 'revoked':
      return <CrossStatusIcon size={g} color="currentColor" />;
    case 'not_requested':
    case 'draft':
      return <InfoStatusIcon size={g} color="currentColor" />;
  }
}

/**
 * Owner verification-state pill (F09 v2). Seven states; labels from
 * `verification:badge.state.<state>` with English `defaultValue`s.
 */
export function VerificationBadge({
  status = 'not_requested',
  size = 'md',
  className,
}: VerificationBadgeProps) {
  const { t } = useI18n();
  const state = normalizeVerificationStatus(status);
  const label = String(
    t(`verification:badge.state.${state}`, { defaultValue: LABEL_DEFAULTS[state] }),
  );
  return (
    <span
      data-state={state}
      className={cn(
        'inline-flex items-center rounded-full border font-mono font-bold uppercase tracking-wider whitespace-nowrap',
        TONE[state],
        SIZE[size],
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex shrink-0',
          state === 'verified' || state === 'pending'
            ? 'text-info'
            : state === 'more_info_needed'
              ? 'text-warning'
              : state === 'rejected' || state === 'revoked'
                ? 'text-danger'
                : 'text-ink-2',
        )}
      >
        {glyphFor(state, GLYPH[size])}
      </span>
      {label}
    </span>
  );
}
